import { useCommon } from "../composition-functions/common";
import { LanguageIsoCode } from "../composition-functions/language/LanguageIsoCode";
import { getGlobalTranslation } from "../i18n";
import { IQueryParams } from "../interfaces/IParams";
import { IQueryListResult } from "../interfaces/IResponses";
import { assertNever } from "../util/assertNever";
import { ComponentDto, ComponentInputDto } from "./dto.component";
import { ComponentType } from "./enum.componentType";
import { ComponentSettings } from "./model.componentSettings";

export interface DeleteComponentParams {
  mandantId: number;
  componentId: number | string;
}

async function deleteComponent({ mandantId, componentId }: DeleteComponentParams) {
  const { deleteRequest } = useCommon();
  await deleteRequest({ path: `/admin/mandants/${mandantId}/components/${componentId}` });
}

async function getActivatableComponents(mandantId: number): Promise<ComponentType[]> {
  const { getRequest } = useCommon();

  const response = await getRequest({ path: `/admin/mandants/${mandantId}/components/getActivatableComponents` });
  const result = await response.json();
  return result;
}

export interface GetComponentParams {
  mandantId: number;
  componentIdOrComponentType: number | string;
}

async function getComponent({ mandantId, componentIdOrComponentType }: GetComponentParams): Promise<ComponentDto> {
  const { getRequest } = useCommon();

  const response = await getRequest({ path: `/admin/mandants/${mandantId}/components/${componentIdOrComponentType}` });
  const result = await response.json();
  return result;
}

interface GetComponentsParams extends IQueryParams {
  mandantId: number;
}

async function getComponents({
  mandantId,
  currentPage,
  pageSize,
  searchTerm,
}: GetComponentsParams): Promise<IQueryListResult<ComponentDto>> {
  const { getRequest } = useCommon();

  const urlSearchParams = new URLSearchParams({
    searchTerm,
    currentPage: currentPage.toString(),
    pageSize: pageSize.toString(),
  });

  const response = await getRequest({ path: `/admin/mandants/${mandantId}/components`, urlSearchParams });
  const result = await response.json();
  return result;
}

interface SaveComponentParams {
  mandantId: number;
  component: ComponentInputDto;
}

async function saveComponent({ mandantId, component }: SaveComponentParams): Promise<{ id: number }> {
  const { postRequest } = useCommon();

  const response = await postRequest({ path: `/admin/mandants/${mandantId}/components`, data: JSON.stringify(component) });
  const result = await response.json();
  return result;
}

const createEmptySettingsForComponentType = (componentType: ComponentType): ComponentSettings => {
  let settings: ComponentSettings;

  switch (componentType) {
    case ComponentType.LTS_TICKETING:
    case ComponentType.YANOVIS:
    case ComponentType.LTS_ASTAT: {
      settings = {
        type: componentType,
        name: getGlobalTranslation({ message: `modules.${componentType}` }),
        config: {
          externalSystemId: 0,
        },
      };
      break;
    }

    case ComponentType.LTS_SUEDTIROL_GUEST_PASS: {
      settings = {
        type: componentType,
        name: getGlobalTranslation({ message: `modules.${componentType}` }),
        config: {
          externalSystemId: 0,
          shouldAutoIssueOnCheckin: false,
        },
      };
      break;
    }

    case ComponentType.VOUCHER: {
      settings = {
        type: componentType,
        name: getGlobalTranslation({ message: `modules.${componentType}` }),
        config: {
          shippingMethods: [],
          paymentSystemIds: [],
        },
      };
      break;
    }

    case ComponentType.CRM_SYNC: {
      settings = {
        type: componentType,
        name: getGlobalTranslation({ message: `modules.${componentType}` }),
        config: {
          username: "",
          password: "",
        },
      };
      break;
    }

    case ComponentType.NEWSLETTER_SYNC: {
      settings = {
        type: componentType,
        name: getGlobalTranslation({ message: `modules.${componentType}` }),
        config: {
          outgoingActions: {
            shouldDelete: true,
            shouldInsert: true,
            shouldUpdate: true,
          },
          incomingActions: {
            shouldUpdate: true,
            shouldDelete: false,
            shouldInsert: false,
          },
          redirectUrlAfterDoubleOptIn: {
            [LanguageIsoCode.GERMAN]: "",
            [LanguageIsoCode.ITALIAN]: "",
            [LanguageIsoCode.ENGLISH]: "",
          },
        },
        meta: {
          lastSyncTimestamp: null,
          isRunning: false,
        },
      };
      break;
    }

    case ComponentType.DISCOUNT_CODE: {
      settings = {
        type: componentType,
        name: getGlobalTranslation({ message: `modules.${componentType}` }),
      };
      break;
    }

    case ComponentType.NONE: {
      settings = {
        type: componentType,
      };
      break;
    }

    case ComponentType.REST_API: {
      settings = {
        type: componentType,
        name: getGlobalTranslation({ message: `modules.${componentType}` }),
        credentials: {
          username: "",
          password: "",
        },
        scopes: {
          extras: { read: false, write: false },
          guests: { read: false, write: false },
          reservations: { read: false, write: false },
          rooms: { read: false, write: false },
        },
      };
      break;
    }

    default: {
      assertNever(componentType);
    }
  }

  return settings;
};

export const useComponent = () => {
  return {
    deleteComponent,
    getActivatableComponents,
    getComponent,
    getComponents,
    saveComponent,
    createEmptySettingsForComponentType,
  };
};
